import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../utils/ui-components";
import { MediaItem, MediaItemSegment, SubtitleItem } from "../data/types";
import { SearchResultMatch } from "../server/media";
import { chevronDownIcon } from "../utils/icons";
import { AudioState } from "./audio-player";

@customElement("subtitle-list-view")
export class SubtitleListView extends BaseElement {
    @property()
    item!: MediaItem;

    @property()
    segment!: MediaItemSegment;

    @property()
    subtitles: SubtitleItem[] = [];

    @property()
    subtitleMatches: (SearchResultMatch & { type: "subtitle" })[] = [];

    @property()
    onAudioRequest?: (state: AudioState) => void;

    @state()
    private isExpanded = false;

    updated(changedProperties: Map<string, any>) {
        if (changedProperties.has("isExpanded") && this.isExpanded && this.subtitleMatches?.length) {
            // Wait for render to complete
            setTimeout(() => {
                const firstMatch = this.renderRoot.querySelector(".bg-yellow-50");
                firstMatch?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 0);
        }
    }

    render() {
        if (!this.subtitles?.length) return null;

        // If we have matches
        if (this.subtitleMatches?.length) {
            if (this.isExpanded) {
                // Show all subtitles with matches highlighted
                const matchedIndices = new Set(this.subtitleMatches.map((m) => m.subtitleIndex));
                return html`
                    <div class="mt-3 space-y-1.5 border border-gray-200 rounded-lg p-3">
                        ${this.subtitles.map((subtitle, index) => {
                            const match = this.subtitleMatches.find((m) => m.subtitleIndex == index);
                            return html`
                                <div class="${matchedIndices.has(index) ? "bg-yellow-50 rounded-md" : ""}">
                                    <subtitle-view
                                        .subtitle=${{
                                            ...subtitle,
                                            text: match?.highlights.text || subtitle.text,
                                        }}
                                        .item=${this.item}
                                        .segment=${this.segment}
                                        .onAudioRequest=${this.onAudioRequest}
                                    ></subtitle-view>
                                </div>
                            `;
                        })}
                    </div>
                `;
            }

            // Show only matches + button, sorted by timestamp
            const sortedMatches = [...this.subtitleMatches].sort((a, b) => {
                const subtitleA = this.subtitles[a.subtitleIndex];
                const subtitleB = this.subtitles[b.subtitleIndex];
                return subtitleA.start - subtitleB.start;
            });

            return html`
                <div class="mt-3 space-y-1.5 border border-gray-200 rounded-lg p-3">
                    ${sortedMatches.map(
                        (match) => html`
                            <div class="bg-yellow-50 rounded-md">
                                <subtitle-view
                                    .subtitle=${{
                                        ...this.subtitles[match.subtitleIndex],
                                        text: match.highlights.text || this.subtitles[match.subtitleIndex].text,
                                    }}
                                    .item=${this.item}
                                    .segment=${this.segment}
                                    .onAudioRequest=${this.onAudioRequest}
                                ></subtitle-view>
                            </div>
                        `
                    )}
                    <button
                        @click=${() => (this.isExpanded = true)}
                        class="w-full mt-2 flex justify-center items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors py-1.5"
                        aria-label="Show all subtitles"
                    >
                        <span class="text-sm">All Subtitles</span>
                        <i class="w-5 h-5 icon">${chevronDownIcon}</i>
                    </button>
                </div>
            `;
        }

        // No matches - show button that renders content only when expanded
        return html`
            <div class="mt-3">
                <button
                    @click=${() => (this.isExpanded = !this.isExpanded)}
                    class="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 p-2 w-full"
                >
                    Subtitles
                    <i class="w-5 h-5 icon transition-transform duration-200 ${this.isExpanded ? "rotate-180" : ""}">${chevronDownIcon}</i>
                </button>
                ${this.isExpanded
                    ? html`
                          <div class="space-y-1.5 border border-gray-200 rounded-lg p-3 mt-2">
                              ${this.subtitles.map(
                                  (subtitle) => html`
                                      <subtitle-view
                                          .subtitle=${subtitle}
                                          .item=${this.item}
                                          .segment=${this.segment}
                                          .onAudioRequest=${this.onAudioRequest}
                                      ></subtitle-view>
                                  `
                              )}
                          </div>
                      `
                    : ""}
            </div>
        `;
    }
}
